import { useState } from 'react';
import { Outlet } from 'react-router';

import { SkipLink } from '@/components/SkipLink';
import { EmployeeSidebar } from '@/components/employee/EmployeeSidebar';
import { EmployeeTopbar } from '@/components/employee/EmployeeTopbar';
import { useUploadQueueRuntime } from '@/hooks/upload-queue';

/**
 * The employee portal's shell — ROADMAP.md §6.1.
 *
 * The same skeleton as `AdminLayout` and `ClientLayout`: a fixed sidebar beside
 * a scrolling content column, collapsing to a slide-over below 1024px. **One
 * navigation model, not three** — FINDINGS.md §11.1 found the prototype does the
 * same thing and reflows correctly at 375px, which is why the original plan's
 * bottom tab bar was dropped.
 */
export function EmployeeLayout() {
  const [menuOpen, setMenuOpen] = useState(false);

  /*
   * The photo queue is wired here, at the top of the portal, rather than on the
   * job screen — because the whole point is that it keeps working after the
   * cleaner navigates away from that screen. Mounted once, torn down on sign-out.
   */
  useUploadQueueRuntime();

  return (
    <div className="min-h-screen bg-ink-50">
      <SkipLink />
      {/* Fixed 224px, desktop only. */}
      <aside className="fixed inset-y-0 left-0 hidden w-56 border-r border-ink-300 bg-white lg:block">
        <EmployeeSidebar />
      </aside>

      {menuOpen ? (
        <>
          <div
            className="fixed inset-0 z-30 bg-black/40 lg:hidden"
            aria-hidden="true"
            onClick={() => {
              setMenuOpen(false);
            }}
          />
          <aside
            className="fixed inset-y-0 left-0 z-40 w-64 border-r border-ink-300 bg-white lg:hidden"
            // A dialog while it is a slide-over: it traps the page behind it,
            // and saying so is what lets a screen reader treat it as something
            // to dismiss.
            role="dialog"
            aria-modal="true"
            aria-label="Menu"
          >
            <EmployeeSidebar
              onNavigate={() => {
                setMenuOpen(false);
              }}
            />
          </aside>
        </>
      ) : null}

      <div className="lg:pl-56">
        <EmployeeTopbar
          onOpenMenu={() => {
            setMenuOpen(true);
          }}
        />
        <main id="main" tabIndex={-1}>
          <Outlet />
        </main>
      </div>
    </div>
  );
}
